import { invariant } from '@e-socna/utils';
import { getFragmentData, ThirdPartyTypeEnum } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';

import { FormVariant } from '../enums/variant.enum';

import { usePersonInitialValuesFactory } from './hooks/usePersonInitialValuesFactory.hook';
import { ThirdPartyForm_Person_Fragment } from './fragments/person.fragment';
import { useThirdPartySteps } from './hooks/useSteps.hook';
import { usePersonSubmit } from './hooks/usePersonSubmit.hook';
import { PersonSchema } from './schemas/person.schema';
import type { ThirdPartyFormTab } from './enums/tab.enum';
import type { ThirdPartyForm_Person } from './fragments/person.fragment';
import type { PersonMutationResult } from './types/person-mutation-result.type';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<typeof ThirdPartyForm_Person_Fragment>;
  readonly isInternal: boolean;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: PersonMutationResult) => void;
  readonly personId?: Id;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly ThirdPartyFormTab[];
  readonly title?: string;
}

export const PersonForm = ({
  askMemoBeforeSubmit = true,
  formVariant,
  initialValues: ppInitialValues,
  isInternal,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  personId,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || personId),
    'You must provide a personId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    ThirdPartyForm_Person_Fragment,
    ppInitialValues,
  );

  const { initialValues } = usePersonInitialValuesFactory({
    isInternal,
    initialValues: pInitialValues,
  });

  const { handleSubmit } = usePersonSubmit({
    formVariant,
    handleSuccess,
    initialValues,
    personId,
  });

  const { steps, wizardChildren } = useThirdPartySteps<ThirdPartyForm_Person>({
    isInternal,
    kind: ThirdPartyTypeEnum.Person,
    stepIdsSubset,
    thirdPartyId: personId,
  });

  return (
    <Wizard
      ValidationSchema={PersonSchema}
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
